import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Helmet } from 'react-helmet';
import {
  Box,
  ButtonBase,
  Collapse,
  Container,
  Grid,
  Typography,
  Stack,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Skeleton,
} from '@mui/material';
import {
  AccessTimeOutlined,
  FileUploadOutlined,
  PersonAddOutlined,
  LockOutlined,
  HomeWorkOutlined,
  TimerOutlined,
  QuestionAnswerOutlined,
  HandshakeOutlined,
  Refresh,
  ExpandMore,
  ChevronRight,
  FilterList,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
  actionMenuMenuListSx,
  getActionMenuListContainerSx,
  getActionMenuPaperSx,
  getDashboardAccountMenuItemSx,
} from '../utils/actionMenuStyles';
import Cookies from 'js-cookie';
import { useGlobalContext } from '../context/GlobalContext';
import { useGlobalAuthContext } from '../context/AuthContext';
import { useGetAllUsersQuery, useGetSavedLayoutByPartnerIdQuery, usePartnersQuery } from '../Slices/roomNateApis';
import { EventLegend } from '../Components/Analytics/EventLegend';
import PartnerAnalyticsGuide from '../Components/Analytics/PartnerAnalyticsGuide';
import { FeedCapBanner } from '../Components/Analytics/FeedStatusHints';
import EngagementHeroStrip from '../Components/Analytics/EngagementHeroStrip';
import { countUniqueVisitors } from '../Components/Analytics/UniqueVisitorsCard';
import { buildSignupUserDirectory } from '../Components/Analytics/accountCreatedEnrichment';
import {
  fetchListingsSummary,
  fetchListingLayoutLinks,
  deleteListingLayoutLink,
  analyticsAuthHeaders,
} from '../Components/Analytics/analyticsListingApi';
import {
  layoutLinkByUuid,
  layoutCustomerRefByUuid,
} from '../Components/Analytics/layoutLinkUtils';
import AttachListingModal from '../Components/Analytics/AttachListingModal';
import ListingManageModal from '../Components/Analytics/ListingManageModal';
import FloorPlanLibrarySection from '../Components/Analytics/FloorPlanLibrarySection';
import LinkByUrlModal from '../Components/Analytics/LinkByUrlModal';
import UnlinkConfirmDialog from '../Components/Analytics/UnlinkConfirmDialog';
import { buildLayoutStatsFromFeed } from '../Components/Analytics/buildLayoutStatsFromFeed';
import { buildLayoutStatsWithLineage } from '../Components/Analytics/buildLayoutStatsWithLineage';
import {
  applyMetricScopeFilters,
  isMetricScopeFilterActive,
} from '../Components/Analytics/metricScopeFilters';
import { METRIC_SCOPES } from '../utils/eventAttribution';
import AnalyticsFilterSheet from '../Components/Analytics/AnalyticsFilterSheet';
import AnalyticsScopeZone from '../Components/Analytics/AnalyticsScopeZone';
import LinkedListingsSummaryBand from '../Components/Analytics/LinkedListingsSummaryBand';
import {
  countLinkedPlans,
  rollupLinkedListings,
} from '../Components/Analytics/linkedListingRollup';
import {
  filterListingsByStatus,
  getLinkedStatusFilterLabel,
  LINKED_STATUS_FILTER_ALL,
} from '../Components/Analytics/linkedListingFilters';
import { buildLayoutIdentityMaps, flattenPartnerLayoutsFromApi, normalizePartnerLayout } from '../Components/Analytics/layoutTreeUtils';
import { partnerLayoutRowFromEnrichedLink, linkedCatalogFromListingLinks, mergeCatalogWithLinkedLayouts } from '../Components/Analytics/linkedLayoutCatalog';
import { ANALYTICS_ENDPOINT } from '../utils/eventLogAnalytics';
import {
  getConfiguredPartnerAgentEmailDomains,
  getPartnerAgentEmailDomain,
} from '../utils/tenantConfig';
import {
  MF_TERMS_URL,
  MF_PRIVACY_URL,
  MF_SUPPORT_URL,
  legalLinkSx,
} from '../utils/legalLinks';
import MetricCard from '../Components/Analytics/MetricCard';
import { groupEventsByUpload } from '../Components/Analytics/FeedList';
import { summarizeSubmissionRows } from '../Components/Analytics/submissionStats';
import { fetchAllOccurrencesForEvent } from '../Components/Analytics/analyticsTimeseriesFetch';
import { getEventInfo } from '../Components/Analytics/eventDict';
import MetricDrillModal from '../Components/Analytics/MetricDrillModal';
import EventDetailModal from '../Components/Analytics/EventDetailModal';
import ListingsView from '../Components/Analytics/ListingsView';
import { useEditorStats } from '../Components/Analytics/EditorEngagementCard';
import EngagementCard from '../Components/Analytics/EngagementCard';
import UniqueVisitorsCard from '../Components/Analytics/UniqueVisitorsCard';
import SharingActivityCard from '../Components/Analytics/SharingActivityCard';
import InquiriesActivityCard from '../Components/Analytics/InquiriesActivityCard';
import PlatformUsageSection from '../Components/Analytics/PlatformUsageSection';
import METRIC_DEFINITIONS from '../Components/Analytics/metricDefinitions';
import { chartPeriodBoundsForTimeRange } from '../Components/Analytics/analyticsChartDays';
import { ANALYTICS_HEADLINE_NUMBER_SX } from '../Components/Analytics/analyticsTypography';
import { buildExternalAiUrl } from '../utils/apiConfig';
import {
  getRoleFromAuthToken,
  isMasterAdminJwtRole,
} from '../utils/routeAccess';
import RecentActivitySection from '../Components/Analytics/RecentActivitySection';
import {
  ANALYTICS_GREEN,
  ANALYTICS_RED,
  analyticsRedAlpha,
} from '../Components/Analytics/analyticsColors';

const ANALYTICS_PAGE_TITLE = 'Analytics | MagicFurnish';
const ANALYTICS_PAGE_DESCRIPTION =
  'View platform analytics and engagement metrics.';

function AnalyticsPageHelmet() {
  return (
    <Helmet>
      <title>{ANALYTICS_PAGE_TITLE}</title>
      <meta name="description" content={ANALYTICS_PAGE_DESCRIPTION} />
      <meta property="og:title" content={ANALYTICS_PAGE_TITLE} />
      <meta property="og:description" content={ANALYTICS_PAGE_DESCRIPTION} />
    </Helmet>
  );
}

/**
 * Page background gradient — fixed to the viewport so it never shifts
 * during scroll. Switch the active key between `flat` and `gradient`.
 */
const ANALYTICS_PAGE_BG_VARIANTS = {
  gradient: 'linear-gradient(to bottom, #ffffff 0%, #fafafa 100%)',
  flat: 'linear-gradient(to bottom, #fafafa 0%, #fafafa 100%)',
};
const ANALYTICS_PAGE_BG = ANALYTICS_PAGE_BG_VARIANTS.flat;
// const ANALYTICS_PAGE_BG = ANALYTICS_PAGE_BG_VARIANTS.gradient;

/** Sticky header fade — backdrop layers extend below the bar; content layout unchanged. */
const ANALYTICS_PAGE_HEADER_FADE_EXTEND_BOTTOM = { xs: -20, sm: -28 };
const ANALYTICS_PAGE_HEADER_FADE_MASK_STRONG =
  'linear-gradient(to bottom, #000 70%, transparent 100%)';

const PAGE_SIZE = 300;
/** Max feed pages auto-loaded before we freeze feed-derived metrics (7,500 events). */
const AUTO_FETCH_MAX_PAGES = 25;
const FEED_EVENT_CAP = AUTO_FETCH_MAX_PAGES * PAGE_SIZE;

/**
 * Circular progress ring for the submissions card success rate.
 * Pure SVG — no extra deps, renders sharply at all pixel densities.
 * Rotated -90° so the arc starts at 12 o'clock.
 */
const RingProgress = ({
  value,
  size = 52,
  strokeWidth: strokeWidthProp,
  strokeColor = 'rgba(0,0,0,0.38)',
  labelFontSize,
  sublabel,
}) => {
  const strokeWidth = strokeWidthProp ?? (size >= 96 ? 5.5 : 3.5);
  const r = (size - strokeWidth) / 2;
  const circ = 2 * Math.PI * r;
  const offset = circ - Math.min(100, Math.max(0, value)) / 100 * circ;
  return (
    <Box sx={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <svg
        width={size}
        height={size}
        style={{ display: 'block', transform: 'rotate(-90deg)' }}
        aria-hidden="true"
      >
        {/* Track */}
        <circle
          cx={size / 2} cy={size / 2} r={r}
          fill="none"
          stroke="rgba(0,0,0,0.07)"
          strokeWidth={strokeWidth}
        />
        {/* Progress arc */}
        <circle
          cx={size / 2} cy={size / 2} r={r}
          fill="none"
          stroke={strokeColor}
          strokeWidth={strokeWidth}
          strokeDasharray={circ}
          strokeDashoffset={offset}
          strokeLinecap="round"
        />
      </svg>
      {/* Centered label (percentage + optional sublabel stacked). The outer
          absolute layer covers the entire ring and centres a separate
          inner stack horizontally + vertically. We avoid putting a
          maxWidth on the outer layer because that broke centring (the
          layer collapsed to maxWidth while still anchored at left:0,
          producing the off-centre "X% SUCCESS" partners flagged). The
          inner stack carries the width constraint instead. */}
      <Box
        sx={{
          position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            maxWidth: `${size * 0.78}px`,
          }}
        >
          <Typography
            sx={{
              fontSize: labelFontSize ?? (size <= 48 ? '0.6rem' : '0.65rem'),
              fontWeight: 700,
              color: 'rgba(0,0,0,0.88)',
              lineHeight: 1,
              letterSpacing: '-0.02em',
            }}
          >
            {value}%
          </Typography>
          {sublabel && (
            <Typography
              sx={{
                fontSize: size >= 72 ? '0.58rem' : '0.5rem',
                fontWeight: 500,
                color: 'rgba(0,0,0,0.42)',
                lineHeight: 1,
                mt: '3px',
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
              }}
            >
              {sublabel}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
};

/**
 * Format seconds for the submissions card avg. processing time row.
 * e.g. 45 → "45 sec.", 90 → "2 min."
 */
const formatProcessingTime = (secs) => {
  if (secs == null) return null;
  if (secs < 60) return `${Math.round(secs)} sec.`;
  return `${Math.round(secs / 60)} min.`;
};

// ── CSV export helpers ─────────────────────────────────────────────────────
const csvEscape = (v) => {
  const s = String(v ?? '');
  return s.includes(',') || s.includes('"') || s.includes('\n')
    ? `"${s.replace(/"/g, '""')}"`
    : s;
};
const downloadCSV = (filename, headers, rows) => {
  const csv = [headers, ...rows]
    .map((r) => r.map(csvEscape).join(','))
    .join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

const arrayToMap = (array, keyField = '_id', valueField = 'count') => {
  if (!Array.isArray(array)) return {};
  return array.reduce(
    (map, item) => {
      const key = item[keyField];
      const value = item[valueField];
      map[key] = value;
      return map;
    },
    {
      shuffle_room: 0,
      'mf:complete': 0,
      download_layout: 0,
      share_layout: 0,
      link_shared: 0,
      furnish_room: 0,
      save_layout: 0,
      layout_saved: 0,
    }
  );
};

const TIME_RANGES = [
  { value: '24h', label: 'Last 24 hours' },
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
];

/** Matches AnalyticsFilterSheet panel width so header menus align. */
const ANALYTICS_HEADER_MENU_WIDTH = {
  xs: 'min(300px, calc(100vw - 16px))',
  sm: 320,
};

/**
 * Date range selector styled as a chromeless ghost pill.
 *
 * Replaces the floating-label MUI Select that shipped before; same data,
 * less visual weight. Bound directly to `timeRange` so the picker
 * actually drives the filter (the prior `dateRange` state was a no-op).
 */
const DateRangePill = ({ value, onChange, onOpen }) => {
  const theme = useTheme();
  const [anchor, setAnchor] = useState(null);
  const current = TIME_RANGES.find((r) => r.value === value) || TIME_RANGES[1];

  return (
    <>
      <Box
        component="button"
        type="button"
        onClick={(e) => {
          setAnchor(e.currentTarget);
          onOpen?.();
        }}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.75,
          px: 1.5,
          py: 1,
          minHeight: 36,
          borderRadius: '10px',
          bgcolor: 'rgba(0,0,0,0.05)',
          border: 'none',
          color: 'rgba(0,0,0,0.88)',
          fontSize: '0.85rem',
          fontWeight: 500,
          cursor: 'pointer',
          fontFamily: 'inherit',
          transition: 'background 160ms ease',
          '&:hover': { bgcolor: 'rgba(0,0,0,0.08)' },
        }}
      >
        {current.label}
        <ExpandMore sx={{ fontSize: 16, opacity: 0.6 }} />
      </Box>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            elevation: 0,
            sx: {
              ...getActionMenuPaperSx(theme, 'dropdown'),
              mt: 1,
              p: 0.75,
              width: ANALYTICS_HEADER_MENU_WIDTH,
              maxWidth: ANALYTICS_HEADER_MENU_WIDTH,
              minWidth: 'unset',
            },
          },
        }}
        MenuListProps={{
          sx: {
            ...getActionMenuListContainerSx('embedded', { uniformSpacing: true }),
            ...actionMenuMenuListSx,
            maxHeight: 'none',
            p: 0,
          },
        }}
      >
        {TIME_RANGES.map((r) => (
          <MenuItem
            key={r.value}
            selected={r.value === value}
            onClick={() => {
              onChange(r.value);
              setAnchor(null);
            }}
            sx={{
              ...getDashboardAccountMenuItemSx(theme),
              ...(r.value === value && {
                fontWeight: 600,
                color: 'rgba(0,0,0,0.88)',
                bgcolor: 'rgba(0,0,0,0.06)',
                '&.Mui-selected': {
                  bgcolor: 'rgba(0,0,0,0.06)',
                },
                '&.Mui-selected:hover': {
                  bgcolor: 'rgba(0,0,0,0.08)',
                },
              }),
            }}
          >
            {r.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

/**
 * Admin-only partner switcher rendered as a discreet subtitle pill.
 *
 * Replaces the noisy floating-label `<Select>` that shipped on staging.
 * Non-admin users never see this — they get a plain text subtitle that
 * matches their auto-resolved tenant.
 */
const PartnerSubtitlePill = ({ value, partners, onChange }) => {
  const [anchor, setAnchor] = useState(null);
  const allOption = { _id: '__all__', domain: 'all', title: 'All Partners' };
  const options = [allOption, ...(Array.isArray(partners) ? partners : [])];
  const current = options.find((p) => p.domain === value) || allOption;

  return (
    <>
      <Box
        component="button"
        type="button"
        onClick={(e) => setAnchor(e.currentTarget)}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          mt: 0.25,
          px: 1,
          py: 0.25,
          ml: -1,
          borderRadius: '8px',
          bgcolor: 'transparent',
          border: 'none',
          color: 'rgba(0,0,0,0.5)',
          fontWeight: 600,
          letterSpacing: 0.5,
          textTransform: 'uppercase',
          fontSize: '0.72rem',
          cursor: 'pointer',
          fontFamily: 'inherit',
          transition: 'background 160ms ease, color 160ms ease',
          '&:hover': { bgcolor: 'rgba(0,0,0,0.04)', color: 'rgba(0,0,0,0.88)' },
        }}
      >
        {current.title || current.domain}
        <ExpandMore sx={{ fontSize: 14, opacity: 0.7 }} />
      </Box>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          sx: {
            mt: 1,
            borderRadius: '10px',
            boxShadow: '0 6px 24px rgba(0,0,0,0.08), 0 1px 3px rgba(0,0,0,0.06)',
            border: '1px solid rgba(0,0,0,0.06)',
            minWidth: 220,
            maxHeight: 360,
          },
        }}
      >
        {options.map((p) => (
          <MenuItem
            key={p._id}
            selected={p.domain === value}
            onClick={() => {
              onChange(p.domain);
              setAnchor(null);
            }}
            sx={{ fontSize: '0.85rem' }}
          >
            {p.title || p.domain}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};


const FEED_INTERVAL = 'day';

const EXCLUDE_INTERNAL_STORAGE_KEY = 'mf:analytics:excludeInternal';
const EXCLUDE_PARTNER_AGENTS_STORAGE_KEY = 'mf:analytics:excludePartnerAgents';

const readInitialExcludeInternal = () => {
  try {
    const raw = window.localStorage.getItem(EXCLUDE_INTERNAL_STORAGE_KEY);
    return raw === 'true';
  } catch {
    return false;
  }
};

const readInitialExcludePartnerAgents = () => {
  try {
    const raw = window.localStorage.getItem(EXCLUDE_PARTNER_AGENTS_STORAGE_KEY);
    return raw === 'true';
  } catch {
    return false;
  }
};

const Analytics = () => {
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));
  const [timeRange, setTimeRange] = useState('7d');

  /** Exclude @magicfurnish.com from buyer engagement and sign-up KPIs. */
  const [excludeInternal, setExcludeInternal] = useState(readInitialExcludeInternal);
  /** Exclude partner-agent emails (e.g. @serhant.com) from buyer engagement KPIs. */
  const [excludePartnerAgents, setExcludePartnerAgents] = useState(
    readInitialExcludePartnerAgents
  );
  useEffect(() => {
    try {
      window.localStorage.setItem(
        EXCLUDE_INTERNAL_STORAGE_KEY,
        excludeInternal ? 'true' : 'false'
      );
    } catch {
      // Storage unavailable — toggle still works in-session.
    }
  }, [excludeInternal]);
  useEffect(() => {
    try {
      window.localStorage.setItem(
        EXCLUDE_PARTNER_AGENTS_STORAGE_KEY,
        excludePartnerAgents ? 'true' : 'false'
      );
    } catch {
      // Storage unavailable — toggle still works in-session.
    }
  }, [excludePartnerAgents]);

  const { activePartner, setActivePartner } = useGlobalContext();
  const { currentUserSessionStorage } = useGlobalAuthContext();

  const userRole = useMemo(
    () => getRoleFromAuthToken(currentUserSessionStorage),
    [currentUserSessionStorage]
  );
  const isAdmin = isMasterAdminJwtRole(userRole);
  const isPartnerAdmin = userRole === 'partnerAdmin';
  const canViewAnalytics = isAdmin || isPartnerAdmin;
  /** Recent Activity opens by default; session count differs by breakpoint in RecentActivitySection. */
  const [recentActivityOpen, setRecentActivityOpen] = useState(true);
  const [filterSheetOpen, setFilterSheetOpen] = useState(false);

  const { data: partnersData } = usePartnersQuery('', {
    skip: !currentUserSessionStorage || !isAdmin,
  });

  /**
   * Subtitle reflects the actual scope of the data on screen as a URL/origin
   * so the page is unambiguous across registered AND unregistered subdomains.
   *  - Admin viewing 'all'    → "All Partners"
   *  - Admin viewing a tenant → that tenant's domain (activePartner is the domain string)
   *  - PartnerAdmin           → the literal hostname they're on
   *
   * The header partner switcher (PartnerSubtitlePill) still surfaces the
   * partner brand title — that is intentional and unchanged.
   */
  const subtitle = isAdmin
    ? activePartner === 'all' || !activePartner
      ? 'All Partners'
      : activePartner
    : window.location.hostname;

  /**
   * Backend partner filter, preserved verbatim from staging:
   * admins use `activePartner` (tenant short-id or 'all'); everyone else
   * sends the literal hostname so the API filters on origin.
   */
  const partnerNameFilter = isAdmin ? activePartner || 'all' : window.location.hostname;

  /**
   * `/user/get-all` partner filter. Backend rules (confirmed by BE):
   *   • partnerAdmin → omit `?partner=` (BE scopes by JWT regardless)
   *   • admin → must pass partner Mongo `_id`; domain strings return empty
   *   • admin viewing 'all' → omit so we fetch every user once
   *
   * Resolving via the loaded partners list lets us accept either a domain or
   * an `_id` from `activePartner` (the dropdown can hand back either) and
   * always emit the `_id`. Returns `null` when we should not pass the param.
   */
  const usersPartnerFilter = useMemo(() => {
    if (!isAdmin) return null;
    if (!activePartner || activePartner === 'all') return null;
    if (!Array.isArray(partnersData)) return null;
    const match = partnersData.find(
      (p) => p?._id === activePartner || p?.domain === activePartner
    );
    return match?._id || null;
  }, [isAdmin, activePartner, partnersData]);

  /** Mongo partner _id for savedLayout/getByPartner (cookie for partner admins). */
  const listingLinkPartnerId = useMemo(() => {
    if (isPartnerAdmin) return Cookies.get('partner') || null;
    return usersPartnerFilter;
  }, [isPartnerAdmin, usersPartnerFilter]);

  /** Floor plans + listing link UI — partner admins always; master admins when one partner is selected. */
  const canManageListingLinks = useMemo(() => {
    if (isPartnerAdmin) return Boolean(listingLinkPartnerId);
    if (!isAdmin) return false;
    if (!activePartner || activePartner === 'all') return false;
    return Boolean(listingLinkPartnerId);
  }, [isPartnerAdmin, isAdmin, listingLinkPartnerId, activePartner]);

  const [categoryCounts, setCategoryCounts] = useState({});
  const [overviewLoading, setOverviewLoading] = useState(true);
  const [overviewError, setOverviewError] = useState(null);

  const [feedRows, setFeedRows] = useState([]);
  const [feedTotal, setFeedTotal] = useState(0);
  const [feedPage, setFeedPage] = useState(0);
  const [feedLoading, setFeedLoading] = useState(false);
  /** True only while page 1 is loading after a window/partner change — not background pagination. */
  const [feedReplaceLoading, setFeedReplaceLoading] = useState(true);
  const [feedError, setFeedError] = useState(null);

  // ── Visitor stats (Unique Visitors, New vs Returning) ─────────────────
  const [visitorStats, setVisitorStats] = useState(null);
  const [listingsSummaryPeriod, setListingsSummaryPeriod] = useState(null);
  const [listingsSummaryLifetime, setListingsSummaryLifetime] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [listingsSummaryLoading, setListingsSummaryLoading] = useState(false);
  const [listingLayoutLinks, setListingLayoutLinks] = useState([]);
  const [catalogLayouts, setCatalogLayouts] = useState([]);

  /** Page-1 partner catalog (no date filter) — uploader lineage for inquiry drill-down. */
  const { data: uploaderCatalogPrefetch, isFetching: uploaderCatalogPrefetchLoading } =
    useGetSavedLayoutByPartnerIdQuery(
      { id: listingLinkPartnerId, page: 1 },
      { skip: !canViewAnalytics || !listingLinkPartnerId }
    );

  const catalogForInquiries = useMemo(() => {
    const linked = linkedCatalogFromListingLinks(listingLayoutLinks);
    const prefetched = Array.isArray(uploaderCatalogPrefetch?.layouts)
      ? uploaderCatalogPrefetch.layouts
      : [];
    const library = Array.isArray(catalogLayouts) ? catalogLayouts : [];
    return mergeCatalogWithLinkedLayouts(
      mergeCatalogWithLinkedLayouts(library, prefetched),
      linked
    );
  }, [catalogLayouts, listingLayoutLinks, uploaderCatalogPrefetch]);

  const catalogLookupLoading =
    uploaderCatalogPrefetchLoading && catalogForInquiries.length === 0;

  const [attachModalOpen, setAttachModalOpen] = useState(false);
  const [attachSelectedPlan, setAttachSelectedPlan] = useState(null);
  const [attachListingId, setAttachListingId] = useState('');
  const [linkByUrlOpen, setLinkByUrlOpen] = useState(false);
  const [statusSetupOpen, setStatusSetupOpen] = useState(false);
  const [statusSetupTarget, setStatusSetupTarget] = useState(null);
  const [linkSuccessMsg, setLinkSuccessMsg] = useState(null);
  const [linkNoticeIsError, setLinkNoticeIsError] = useState(false);

  // ── Download conversion rate ───────────────────────────────────────────
  // ── Platform Usage (admin-only: monthly-summary) ───────────────────────
  const [monthlySummary, setMonthlySummary] = useState([]);
  const [monthlyGenerationLimit, setMonthlyGenerationLimit] = useState(null);
  const [monthlySummaryRequested, setMonthlySummaryRequested] = useState(false);


  // ── Mobile Safari theme-color ──────────────────────────────────────────
  // Sets the status-bar / address-bar chrome to match the page background
  // (#fafafa) while /analytics is mounted, then restores the app-wide
  // default (white) on unmount. Scoped to this page only — no other routes
  // are affected.
  useEffect(() => {
    const meta = document.querySelector('meta[name="theme-color"]');
    if (!meta) return;
    const previous = meta.getAttribute('content');
    meta.setAttribute('content', '#fafafa');
    return () => meta.setAttribute('content', previous || 'white');
  }, []);
  const [sync, setSync] = useState(0);
  const [linkedStatusFilter, setLinkedStatusFilter] = useState(LINKED_STATUS_FILTER_ALL);

  const [activeMetric, setActiveMetric] = useState(null);
  // Sample-listing preview is collapsed by default — the empty placeholder
  // ate screen real estate without telling the partner anything new.
  // Keeping it behind a "Preview sample listing" toggle keeps the layout
  // tight until the real data lands.
  const [businessImpactOpen, setBusinessImpactOpen] = useState(false);
  const [sampleListingOpen, setSampleListingOpen] = useState(false);
  const [activeFeedRow, setActiveFeedRow] = useState(null);
  // Captured alongside the active feed row so EventDetailModal can show the
  // parent group's authoritative gallery URL when an event is opened from
  // inside a card's expanded timeline. Without this, events whose own detail
  // doesn't carry a galleryUrl (everything except gallery_ready) had to
  // construct one locally from a UUID and would render an infinite-spinner
  // link.
  const [activeRowGalleryUrl, setActiveRowGalleryUrl] = useState(null);

  // Recompute from/to on Sync as well as timeRange — otherwise `to` stays
  // pinned to page-open time and refresh only re-fetches a stale window.
  const chartPeriodBounds = useMemo(
    () => chartPeriodBoundsForTimeRange(timeRange, sync),
    [timeRange, sync]
  );

  const dateParams = useMemo(() => {
    const { from: _from, to: _to } = chartPeriodBounds;
    return `from=${_from.toISOString()}&to=${_to.toISOString()}`;
  }, [chartPeriodBounds]);

  /**
   * Stale-request guards. When the user changes filters mid-flight we
   * bump these counters; in-flight handlers compare on resolution and
   * silently drop their result if the counter has advanced — preventing
   * a slower old response from clobbering a newer one.
   */
  const overviewReqRef = useRef(0);
  const feedReqRef = useRef(0);
  const submissionReqRef = useRef(0);
  const [submissionRowsRaw, setSubmissionRowsRaw] = useState([]);
  const [submissionFetchSettled, setSubmissionFetchSettled] = useState(false);

  /**
   * Overview loads on first paint. Pulls only the per-event counts that
   * power the KPI tiles; the heavier `/timeseries` payload stays gated
   * behind the Feed tab to preserve CPU/GPU on first paint.
   */
  const fetchOverview = useCallback(async () => {
    if (!canViewAnalytics) return;
    if (activePartner === '') return;
    const reqId = ++overviewReqRef.current;
    setOverviewLoading(true);
    setOverviewError(null);
    try {
      const countsRes = await fetch(
        `${ANALYTICS_ENDPOINT}/counts?${dateParams}&groupBy=meta.event&partnerName=${partnerNameFilter}`
      );
      const countsData = await countsRes.json();
      if (reqId !== overviewReqRef.current) return;
      if (countsRes.ok) {
        if (!Array.isArray(countsData)) {
          setOverviewError('Analytics backend returned data in an unexpected format — please refresh.');
          return;
        }
        setCategoryCounts(arrayToMap(countsData));
      } else {
        setOverviewError('Failed to sync with analytics backend: ' + countsData?.error);
      }
    } catch (err) {
      if (reqId !== overviewReqRef.current) return;
      setOverviewError('Failed to sync with analytics backend: ' + err.message);
    } finally {
      if (reqId === overviewReqRef.current) setOverviewLoading(false);
    }
  }, [canViewAnalytics, activePartner, dateParams, partnerNameFilter]);

  /**
   * Submissions are sparse compared with gallery traffic, so the paginated
   * activity feed often stops before older upload attempts enter feedRows.
   * Load the full-window upload/failure/ready events up front for the card
   * and drill-down (same walk MetricDrillModal used on open).
   */
  const fetchSubmissionWindow = useCallback(async () => {
    if (!canViewAnalytics) return;
    if (activePartner === '') return;
    const reqId = ++submissionReqRef.current;
    setSubmissionFetchSettled(false);
    try {
      const fetched = await fetchAllOccurrencesForEvent(
        'floorplan_uploaded',
        dateParams,
        partnerNameFilter
      );
      if (reqId !== submissionReqRef.current) return;
      setSubmissionRowsRaw(Array.isArray(fetched) ? fetched : []);
    } catch {
      if (reqId !== submissionReqRef.current) return;
      setSubmissionRowsRaw([]);
    } finally {
      if (reqId === submissionReqRef.current) setSubmissionFetchSettled(true);
    }
  }, [canViewAnalytics, activePartner, dateParams, partnerNameFilter]);

  // ── Visitor stats ──────────────────────────────────────────────────────
  const fetchVisitorStats = useCallback(async () => {
    if (!canViewAnalytics) return;
    if (activePartner === '') return;
    try {
      const res = await fetch(
        `${ANALYTICS_ENDPOINT}/visitor-stats?${dateParams}&partnerName=${partnerNameFilter}`
      );
      if (res.ok) {
        const d = await res.json();
        setVisitorStats(d);
      }
    } catch {
      // Non-critical — card gracefully shows 0
    }
  }, [canViewAnalytics, activePartner, dateParams, partnerNameFilter]);

  const fetchListingsData = useCallback(async () => {
    if (!canViewAnalytics) return;
    if (!isPartnerAdmin && !isAdmin) return;
    if (isPartnerAdmin && activePartner === '') return;
    if (isAdmin && (!activePartner || activePartner === 'all' || !listingLinkPartnerId)) {
      setListingsSummaryPeriod(null);
      setListingsSummaryLifetime(null);
      setListingLayoutLinks([]);
      setListingsSummaryLoading(false);
      return;
    }
    setListingsSummaryLoading(true);
    try {
      const [summaryPeriod, summaryLifetime, links] = await Promise.all([
        fetchListingsSummary(partnerNameFilter, { dateParams }),
        fetchListingsSummary(partnerNameFilter),
        fetchListingLayoutLinks(partnerNameFilter),
      ]);
      setListingsSummaryPeriod(summaryPeriod);
      setListingsSummaryLifetime(summaryLifetime);
      setListingLayoutLinks(Array.isArray(links) ? links : []);
    } catch {
      setListingsSummaryPeriod(null);
      setListingsSummaryLifetime(null);
      setListingLayoutLinks([]);
    } finally {
      setListingsSummaryLoading(false);
    }
  }, [
    canViewAnalytics,
    activePartner,
    dateParams,
    partnerNameFilter,
    isPartnerAdmin,
    isAdmin,
    listingLinkPartnerId,
  ]);

  // ── Platform Usage (admin-only) ─────────────────────────────────────────
  // Monthly summary requires a partnerId (Mongo ObjectId). Resolved from
  // the already-loaded partnersData array (admin) or the JWT cookie (partnerAdmin).
  const fetchMonthlySummary = useCallback(async () => {
    if (!canViewAnalytics) return;

    let partnerId = null;
    let limit = null;

    if (isAdmin) {
      // Master admin: resolve from the full partner list
      if (!activePartner || activePartner === 'all') {
        // Reset so stale data from a previous partner isn't shown
        setMonthlySummary([]);
        setMonthlySummaryRequested(false);
        return;
      }
      const partnerObj = Array.isArray(partnersData)
        ? partnersData.find(
            (p) => p.domain === activePartner || p._id === activePartner
          )
        : null;
      if (!partnerObj?._id) return;
      partnerId = partnerObj._id;
      limit = partnerObj.partnerPackage?.monthlyGenerationLimit ?? null;
    } else if (isPartnerAdmin) {
      // Partner admin: read their own partner ID from the auth cookie
      partnerId = Cookies.get('partner') || null;
      if (!partnerId) return;
    } else {
      return;
    }

    setMonthlyGenerationLimit(limit);
    setMonthlySummaryRequested(true);

    try {
      const base = buildExternalAiUrl('/api/v2/log');
      // Fetch a full 12-month window for the trend chart
      const twelveMonthsAgo = new Date();
      twelveMonthsAgo.setMonth(twelveMonthsAgo.getMonth() - 12);
      const from = twelveMonthsAgo.toISOString();
      const to = new Date().toISOString();
      const res = await fetch(
        `${base}/monthly-summary?partnerId=${partnerId}&from=${from}&to=${to}`
      );
      if (res.ok) {
        const d = await res.json();
        if (Array.isArray(d)) setMonthlySummary(d);
      }
    } catch {
      // Non-critical — section will show empty state
    }
  }, [isAdmin, isPartnerAdmin, canViewAnalytics, activePartner, partnersData]);

  /**
   * Feed-only fetch. Gated behind `feedEverOpened` so the heavier per-row
   * payload doesn't hit the network until the user actually opens the tab.
   *
   * Server-side pagination preserved from staging — page 1 replaces, later
   * pages append. Response can be either an array or `{ data/items/results,
   * total/totalCount/count }`.
   */
  const fetchFeedPage = useCallback(
    async (pageZeroIndexed, mode = 'replace') => {
      if (!canViewAnalytics) return;
      /** Bump on replace so a stale page-1 from old filters can't overwrite
       * fresh data; appends share the same counter so a stale "Load more"
       * from a previous filter is also dropped. */
      const reqId = mode === 'replace' ? ++feedReqRef.current : feedReqRef.current;
      if (mode === 'replace') {
        setFeedReplaceLoading(true);
      }
      setFeedLoading(true);
      setFeedError(null);
      try {
        const res = await fetch(
          `${ANALYTICS_ENDPOINT}/timeseries?${dateParams}&interval=${FEED_INTERVAL}&partnerName=${partnerNameFilter}&page=${pageZeroIndexed + 1}&pageSize=${PAGE_SIZE}`,
          // Carry the admin/partnerAdmin token so BE PR #185 includes the
          // captured-artifact link (detail.uploadedFileUrl) on failed rows;
          // anonymous requests get it stripped. /timeseries stays optional-auth.
          { headers: analyticsAuthHeaders() }
        );
        const body = await res.json();
        if (reqId !== feedReqRef.current) return;
        if (!res.ok) {
          setFeedError('Failed to load feed: ' + (body?.error || res.statusText));
          return;
        }

        let rows = [];
        let total = 0;
        if (Array.isArray(body)) {
          rows = body;
          total = body.length;
        } else {
          rows = body?.data || body?.items || body?.results || [];
          total = body?.total ?? body?.totalCount ?? body?.count ?? rows.length;
        }

        setFeedTotal(Number.isFinite(total) ? total : rows.length);
        setFeedRows((prev) => {
          if (mode === 'replace') return Array.isArray(rows) ? rows : [];
          const seen = new Set(prev.map((r) => r._id).filter(Boolean));
          const next = [...prev];
          (rows || []).forEach((r) => {
            if (!r._id || !seen.has(r._id)) next.push(r);
          });
          return next;
        });
      } catch (err) {
        if (reqId !== feedReqRef.current) return;
        setFeedError('Failed to load feed: ' + err.message);
      } finally {
        if (reqId === feedReqRef.current) {
          setFeedLoading(false);
          if (mode === 'replace') setFeedReplaceLoading(false);
        }
      }
    },
    [canViewAnalytics, dateParams, partnerNameFilter]
  );

  useEffect(() => {
    fetchOverview();
  }, [fetchOverview, sync]);

  useEffect(() => {
    fetchSubmissionWindow();
  }, [fetchSubmissionWindow, sync]);

  useEffect(() => { fetchVisitorStats(); }, [fetchVisitorStats, sync]);
  useEffect(() => { fetchListingsData(); }, [fetchListingsData, sync]);
  useEffect(() => { fetchMonthlySummary(); }, [fetchMonthlySummary, sync]);

  /**
   * Feed loads on mount together with the overview — both fire in parallel.
   * No lazy-gate needed in a single-scroll layout since the content is
   * always visible. The overview skeleton resolves first (lighter query);
   * the feed section shows its own skeleton while the timeseries loads.
   */
  useEffect(() => {
    setFeedPage(0);
    setFeedRows([]);
    setFeedTotal(0);
    fetchFeedPage(0, 'replace');
  }, [fetchFeedPage, sync]);

  /**
   * Auto-pagination: once page 1 lands, keep fetching subsequent pages in
   * the background until the full window is loaded. Several derived
   * metrics on the dashboard (Editor Sessions, Unique Visitors, the new
   * interactive-session model, the average processing time) are computed
   * locally from feedRows, so without this they only reflected the first
   * page's events and partners reading a 30-day window would see numbers
   * that silently undercount activity.
   *
   * Safety:
   *   • The auto-fetch is gated on `!feedLoading` so the user's manual
   *     "Load more" never races with it.
   *   • Each replace bumps feedReqRef in fetchFeedPage; stale appends
   *     drop themselves on resolution.
   *   • Capped at AUTO_FETCH_MAX_PAGES so a corrupt totalCount can't put
   *     us into an unbounded fetch loop.
   */
  useEffect(() => {
    if (!canViewAnalytics) return undefined;
    if (feedLoading) return undefined;
    if (feedRows.length === 0) return undefined;
    if (feedTotal === 0) return undefined;
    if (feedRows.length >= feedTotal) return undefined;
    if (feedPage + 1 >= AUTO_FETCH_MAX_PAGES) return undefined;

    // Small debounce so back-to-back state updates from the previous page
    // don't spam the network. The next fetch flips feedLoading back to
    // true, which makes this effect re-skip until that page completes.
    const id = setTimeout(() => {
      const next = feedPage + 1;
      setFeedPage(next);
      fetchFeedPage(next, 'append');
    }, 0);
    return () => clearTimeout(id);
  }, [
    canViewAnalytics,
    feedLoading,
    feedRows.length,
    feedTotal,
    feedPage,
    fetchFeedPage,
  ]);

  /**
   * Full feed window loaded (or cap hit). Background page fetches must not
   * flip this — only the initial replace after a filter/window change.
   */
  const feedFetchSettled = useMemo(() => {
    if (feedReplaceLoading) return false;
    if (feedTotal === 0) return !feedLoading;
    if (feedRows.length >= feedTotal) return true;
    if (feedPage + 1 >= AUTO_FETCH_MAX_PAGES) return true;
    return false;
  }, [feedReplaceLoading, feedLoading, feedTotal, feedRows.length, feedPage]);

  /** True when we hit the event cap but more events exist in the window. */
  const feedCapped = useMemo(
    () =>
      feedFetchSettled &&
      feedTotal > 0 &&
      feedRows.length < feedTotal &&
      feedPage + 1 >= AUTO_FETCH_MAX_PAGES,
    [feedFetchSettled, feedTotal, feedRows.length, feedPage]
  );

  /** Full feed available — safe for per-user activity stats in drill-downs. */
  const feedActivityReady = feedFetchSettled && !feedCapped;

  const usersQueryArg =
    !usersPartnerFilter || usersPartnerFilter === 'all' ? null : usersPartnerFilter;
  const { data: partnerUsersRaw } = useGetAllUsersQuery(usersQueryArg, {
    skip: !canViewAnalytics,
  });
  const partnerAdminUsers = useMemo(() => {
    if (!Array.isArray(partnerUsersRaw)) return [];
    return partnerUsersRaw;
  }, [partnerUsersRaw]);

  // ── Scoped KPI rows (supply / demand / account) ───────────────────────
  const scopeFilterBase = useMemo(
    () => ({
      excludeInternal,
      excludeAgentActivity: excludePartnerAgents,
      partnerHostname: partnerNameFilter,
      partnerAdminUsers,
      contextRows: feedRows,
    }),
    [excludeInternal, excludePartnerAgents, partnerNameFilter, partnerAdminUsers, feedRows]
  );

  const supplyMetricsRows = useMemo(
    () =>
      applyMetricScopeFilters(submissionRowsRaw, {
        ...scopeFilterBase,
        scope: METRIC_SCOPES.supply,
        excludeInternal: false,
        excludeAgentActivity: false,
      }),
    [submissionRowsRaw, scopeFilterBase]
  );

  const demandMetricsRows = useMemo(
    () =>
      applyMetricScopeFilters(feedRows, {
        ...scopeFilterBase,
        scope: METRIC_SCOPES.demand,
      }),
    [feedRows, scopeFilterBase]
  );

  const accountMetricsRows = useMemo(
    () =>
      applyMetricScopeFilters(feedRows, {
        ...scopeFilterBase,
        scope: METRIC_SCOPES.account,
        excludeAgentActivity: false,
      }),
    [feedRows, scopeFilterBase]
  );

  const demandScopeFilterActive = isMetricScopeFilterActive(
    METRIC_SCOPES.demand,
    excludeInternal,
    excludePartnerAgents
  );

  const partnerAgentEmailDomain = useMemo(() => {
    const fromHost = getPartnerAgentEmailDomain(partnerNameFilter);
    if (fromHost) return fromHost;
    const configured = getConfiguredPartnerAgentEmailDomains();
    return configured.length > 0 ? configured[0] : null;
  }, [partnerNameFilter]);

  const listingPerformanceCaption = 'Linked listing totals — not affected by Filters.';

  const linkedPlanCount = useMemo(
    () => countLinkedPlans(listingLayoutLinks),
    [listingLayoutLinks]
  );

  const filteredListingsPeriod = useMemo(
    () =>
      filterListingsByStatus(
        listingsSummaryPeriod?.listings,
        linkedStatusFilter
      ),
    [listingsSummaryPeriod, linkedStatusFilter]
  );

  const filteredListingsLifetime = useMemo(
    () =>
      filterListingsByStatus(
        listingsSummaryLifetime?.listings,
        linkedStatusFilter
      ),
    [listingsSummaryLifetime, linkedStatusFilter]
  );

  const linkedListingsPeriodRollup = useMemo(
    () => rollupLinkedListings(filteredListingsPeriod, { scope: 'period' }),
    [filteredListingsPeriod]
  );

  const linkedListingsLifetimeRollup = useMemo(
    () => rollupLinkedListings(filteredListingsLifetime, { scope: 'lifetime' }),
    [filteredListingsLifetime]
  );

  const linkedStatusFilterLabel = useMemo(
    () => getLinkedStatusFilterLabel(linkedStatusFilter),
    [linkedStatusFilter]
  );

  const filteredLinkedCount = useMemo(() => {
    if (linkedStatusFilter === LINKED_STATUS_FILTER_ALL) return linkedPlanCount;
    return filteredListingsPeriod.length;
  }, [linkedStatusFilter, linkedPlanCount, filteredListingsPeriod]);

  const countEventInRows = useCallback((event, rows) => {
    if (!Array.isArray(rows)) return 0;
    let n = 0;
    for (let i = 0; i < rows.length; i += 1) {
      if (rows[i]?.meta?.event === event) n += 1;
    }
    return n;
  }, []);
  const countFromDemand = useCallback(
    (event) => countEventInRows(event, demandMetricsRows),
    [countEventInRows, demandMetricsRows]
  );
  const countFromAccount = useCallback(
    (event) => countEventInRows(event, accountMetricsRows),
    [countEventInRows, accountMetricsRows]
  );

  const openMetric = (tile) => {
    setActiveMetric(tile);
  };

  const handleSync = () => setSync((n) => n + 1);

  const handleSelectPartner = (id) => {
    setActivePartner(id);
    Cookies.set('activePartner', id, { expires: 30 });
    setFeedPage(0);
  };

  const handleLoadMore = () => {
    const next = feedPage + 1;
    setFeedPage(next);
    fetchFeedPage(next, 'append');
  };

  const handleExportFeedCSV = () => {
    if (!feedRows.length) return;
    const headers = ['Timestamp', 'Event', 'Event Name', 'User', 'Gallery URL', 'Ref'];
    const rows = feedRows.map((r) => [
      r.timestamp || '',
      r.meta?.event || '',
      getEventInfo(r.meta?.event)?.alias || '',
      r.meta?.user || 'guest',
      r.detail?.galleryUrl || r.detail?.url || '',
      r.meta?.customer_ref || '',
    ]);
    downloadCSV('recent-activity.csv', headers, rows);
  };

  /**
   * Featured Submissions card — one count per upload attempt (same rows the
   * drill-down lists), not raw gallery_ready / upload_failed event totals.
   */
  const submissionSummary = useMemo(() => {
    if (overviewError || !submissionFetchSettled) {
      return {
        processed: 0,
        failed: 0,
        pending: 0,
        total: 0,
        resolved: 0,
        successRate: 0,
      };
    }
    return summarizeSubmissionRows(supplyMetricsRows);
  }, [supplyMetricsRows, submissionFetchSettled, overviewError]);

  const processedCount = submissionSummary.processed;
  const failedCount = submissionSummary.failed;
  const submissionsCount = submissionSummary.total;
  const successRate = submissionSummary.resolved > 0
    ? submissionSummary.successRate
    : 0;

  const ringColor =
    submissionsCount === 0
      ? 'rgba(0,0,0,0.38)'
      : successRate >= 60
      ? ANALYTICS_GREEN
      : ANALYTICS_RED;

  /**
   * The clickable config for the Submissions featured card — drives the same
   * MetricDrillModal that individual KPI tiles use.
   */
  const SUBMISSIONS_TILE = {
    title: 'Floor Plan Submissions',
    value: submissionsCount,
    eventKey: 'floorplan_uploaded',
    about: METRIC_DEFINITIONS.submissions,
  };

  // ── Conversion rate (auth wall → sign-up) ─────────────────────────────────
  // Shown in a wide "Conversion" card below the 4 KPI tiles.  Tapping it
  // opens the account_created drill-down with per-user stats.
  // (gallery view count is now consumed by UniqueVisitorsCard directly
  // from feedRows; this aggregate is no longer needed on AnalyticsTwo.)
  // Sign-Ups conversion denominator honors the audience filter so
  // creator self-sign-ups (a partner admin testing) don't inflate the
  // "X prompted → Y converted" copy that ships in the card body.
  const authWallCount = overviewError
    ? 0
    : countFromDemand('auth_wall_triggered');
  const signupCount = overviewError
    ? 0
    : countFromAccount('account_created');
  // "Effective prompted" = auth_wall_triggered OR direct sign-ups via /signup.
  // Using max(authWall, signups) ensures every signup has a corresponding prompt,
  // so conversion is always ≤ 100% even when users bypass the in-editor auth wall.
  const effectivePrompted = Math.max(authWallCount, signupCount);
  const conversionRate = effectivePrompted > 0
    ? Math.round(signupCount * 100 / effectivePrompted)
    : null;

  const CONVERSION_TILE = {
    title: 'Sign-Ups',
    value: signupCount,
    eventKey: 'account_created',
    about: METRIC_DEFINITIONS.conversion,
  };

  // ── Average floor-plan processing time ────────────────────────────────────
  // Uses scoped supply rows so the time shown matches the Submissions count above.
  const avgProcessingSeconds = useMemo(() => {
    const rowsForProcessing =
      submissionFetchSettled ? supplyMetricsRows : demandMetricsRows;
    if (!Array.isArray(rowsForProcessing) || rowsForProcessing.length === 0) return null;
    const groups = groupEventsByUpload(rowsForProcessing);
    const durations = [];
    groups.forEach((group) => {
      const uploaded = group.rows.find((r) => r.meta?.event === 'floorplan_uploaded');
      const ready    = group.rows.find((r) => r.meta?.event === 'gallery_ready');
      if (uploaded?.timestamp && ready?.timestamp) {
        const diff =
          new Date(ready.timestamp).getTime() - new Date(uploaded.timestamp).getTime();
        if (diff > 5000) durations.push(Math.round(diff / 1000));
      }
    });
    if (durations.length === 0) return null;
    return Math.round(durations.reduce((a, b) => a + b, 0) / durations.length);
  }, [submissionFetchSettled, supplyMetricsRows, demandMetricsRows]);

  // Resolve repeat visitors to one identity: the full feed carries the
  // account_created events that let us merge a person's guest sessions and
  // multiple Mongo ids by email, so the count isn't inflated by duplicates.
  const visitorUserDirectory = useMemo(
    () => buildSignupUserDirectory(partnerAdminUsers),
    [partnerAdminUsers]
  );

  const snapshotUniqueVisitors = useMemo(() => {
    if (feedReplaceLoading) return null;
    const computed = countUniqueVisitors(demandMetricsRows, {
      allRows: feedRows,
      directory: visitorUserDirectory,
    });
    if (!feedFetchSettled) {
      if (!demandScopeFilterActive && visitorStats?.uniqueVisitors > 0) {
        return visitorStats.uniqueVisitors;
      }
      return null;
    }
    return computed ?? 0;
  }, [
    demandMetricsRows,
    feedRows,
    visitorUserDirectory,
    feedReplaceLoading,
    feedFetchSettled,
    visitorStats,
    demandScopeFilterActive,
  ]);

  /** Listing performance uses the full feed — not audience-filtered. */
  const listingLayoutStatsWithLineage = useMemo(
    () => buildLayoutStatsWithLineage(catalogLayouts, feedRows),
    [catalogLayouts, feedRows]
  );

  const layoutUuidByMongoId = useMemo(
    () => buildLayoutIdentityMaps(catalogLayouts).uuidByMongoId,
    [catalogLayouts]
  );

  /**
   * Join `listing-layout-links` × `getByPartner` so each Listings row can show
   * the floor plan thumbnail + name instead of just the opaque customer_ref.
   * If multiple links share a customer_ref (root + client forks), prefer the
   * root layout (not a fork) so admins see the original upload.
   */
  const planByCustomerRef = useMemo(() => {
    const out = new Map();
    if (!Array.isArray(listingLayoutLinks) || listingLayoutLinks.length === 0) {
      return out;
    }
    const planByUuid = new Map();
    (catalogLayouts || []).forEach((raw) => {
      const n = normalizePartnerLayout(raw);
      if (n?.uuid) planByUuid.set(n.uuid, n);
    });
    listingLayoutLinks.forEach((link) => {
      const ref = link?.customer_ref;
      const uuid = link?.layoutUuid;
      if (!ref || !uuid) return;
      let plan = planByUuid.get(uuid);
      if (!plan && link?.layout) {
        plan = normalizePartnerLayout(partnerLayoutRowFromEnrichedLink(link.layout));
      }
      if (!plan) return;
      const prev = out.get(ref);
      if (!prev || (prev.isFork && !plan.isFork)) {
        out.set(ref, plan);
      }
    });
    return out;
  }, [listingLayoutLinks, catalogLayouts]);

  // eslint-disable-next-line no-unused-vars
  const layoutLinksByUuid = useMemo(
    () => layoutLinkByUuid(listingLayoutLinks),
    [listingLayoutLinks]
  );

  const layoutRefByUuid = useMemo(
    () => layoutCustomerRefByUuid(listingLayoutLinks),
    [listingLayoutLinks]
  );

  const openAttachModal = useCallback((plan, listingId = '') => {
    setAttachSelectedPlan(plan);
    setAttachListingId(listingId);
    setAttachModalOpen(true);
  }, []);

  // eslint-disable-next-line no-unused-vars
  const openAttachFromListing = useCallback(
    ({ plan = null, layoutUuid = '', listingId = '' } = {}) => {
      if (plan?.uuid || plan?._id) {
        openAttachModal(plan, listingId);
        return;
      }
      const found = catalogLayouts
        .map(normalizePartnerLayout)
        .find((p) => p?.uuid === layoutUuid);
      if (found) {
        openAttachModal(found, listingId);
        return;
      }
      // No plan resolvable in current catalog window — surface the library
      // instead of opening an empty modal with a misleading placeholder.
      document.getElementById('floor-plan-library')?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    },
    [catalogLayouts, openAttachModal]
  );

  const openFeaturedPlanGallery = useCallback((plan) => {
    const id = plan?._id;
    if (!id || !/^[0-9a-fA-F]{24}$/.test(String(id))) return;
    window.open(
      `${window.location.origin}/floor-gallery/${id}`,
      '_blank',
      'noopener,noreferrer'
    );
  }, []);

  const headerSubdomainLabel =
    !isAdmin && partnerNameFilter && partnerNameFilter !== 'all'
      ? partnerNameFilter
      : !isAdmin
      ? window.location.hostname
      : null;

  const headerIconButtonSx = {
    bgcolor: 'rgba(0,0,0,0.05)',
    borderRadius: '50%',
    width: 32,
    height: 32,
    '&:hover': { bgcolor: 'rgba(0,0,0,0.08)' },
  };

  const handleCatalogLoaded = useCallback((layouts) => {
    setCatalogLayouts(Array.isArray(layouts) ? layouts : []);
  }, []);

  const handleListingLinkSuccess = useCallback((result) => {
    const n = result?.backfill?.modifiedCount;
    const savedStatus =
      result?.listingState?.custom_property_status ||
      result?.listingState?.customPropertyStatus;
    setLinkNoticeIsError(false);
    if (n != null && n > 0) {
      setLinkSuccessMsg(
        `Linked. Updated ${n} past event${n !== 1 ? 's' : ''}.`
      );
    } else if (savedStatus && result?.link) {
      setLinkSuccessMsg('Listing saved');
    } else if (result?.listingState) {
      setLinkSuccessMsg('Listing saved.');
    } else if (result?.updated && result?.link) {
      setLinkSuccessMsg('Listing saved.');
    } else if (result?.link) {
      setLinkSuccessMsg(
        'Floor plan linked. Open Manage listing on the row to add property status.'
      );
    } else {
      setLinkSuccessMsg('Floor plan linked to listing.');
    }
    setSync((s) => s + 1);
  }, []);

  const openListingManage = useCallback((payload) => {
    if (!payload?.linkId) return;
    setStatusSetupTarget(payload);
    setStatusSetupOpen(true);
  }, []);

  const openManageForLinkedPlan = useCallback(
    (plan, listingId) => {
      const ref = String(listingId || '').trim();
      if (!ref || !plan) {
        openAttachModal(plan, listingId);
        return;
      }
      const link = (listingLayoutLinks || []).find(
        (row) => String(row.customer_ref || '').trim() === ref
      );
      if (!link?._id) {
        openAttachModal(plan, listingId);
        return;
      }
      const listingRow =
        listingsSummaryLifetime?.listings?.find(
          (row) => String(row.customer_ref || '').trim() === ref
        ) ||
        listingsSummaryPeriod?.listings?.find(
          (row) => String(row.customer_ref || '').trim() === ref
        );
      openListingManage({
        linkId: link._id,
        listingRef: ref,
        planName: plan?.name,
        mlsStatus: listingRow?.mlsStatus,
        customPropertyStatus: listingRow?.customPropertyStatus,
        onMarketDate: listingRow?.onMarketDate,
        onMarketDateSource: listingRow?.onMarketDateSource,
        offMarketDate: listingRow?.offMarketDate,
        mlsSyncEnabled: listingRow?.mlsSyncEnabled,
      });
    },
    [
      listingLayoutLinks,
      listingsSummaryLifetime,
      listingsSummaryPeriod,
      openAttachModal,
      openListingManage,
    ]
  );

  const closeListingStatusSetup = useCallback(() => {
    setStatusSetupOpen(false);
    setStatusSetupTarget(null);
  }, []);

  const clearLinkNotice = useCallback(() => {
    setLinkSuccessMsg(null);
    setLinkNoticeIsError(false);
  }, []);

  useEffect(() => {
    if (!linkSuccessMsg) return undefined;
    const timer = window.setTimeout(() => clearLinkNotice(), 7000);
    return () => window.clearTimeout(timer);
  }, [linkSuccessMsg, clearLinkNotice]);

  // Unlink confirmation is a single dialog shared by the Listings section and
  // the Floor plans library — both sources call `requestUnlink(payload)` which
  // stashes the intent and opens the dialog. The actual BE delete only runs
  // when the user confirms in `handleConfirmUnlink`.
  const [confirmUnlink, setConfirmUnlink] = useState(null);
  const [unlinkPending, setUnlinkPending] = useState(false);

  const requestUnlink = useCallback((payload) => {
    if (!payload?.linkId) return;
    setConfirmUnlink(payload);
  }, []);

  const handleConfirmUnlink = useCallback(async () => {
    if (!confirmUnlink?.linkId || unlinkPending) return;
    setUnlinkPending(true);
    try {
      await deleteListingLayoutLink(confirmUnlink.linkId, partnerNameFilter);
      setLinkNoticeIsError(false);
      setLinkSuccessMsg('Listing unlinked.');
      setSync((s) => s + 1);
      setConfirmUnlink(null);
    } catch (e) {
      setLinkNoticeIsError(true);
      setLinkSuccessMsg(e?.message || 'Could not unlink listing.');
    } finally {
      setUnlinkPending(false);
    }
  }, [confirmUnlink, partnerNameFilter, unlinkPending]);

  const cancelUnlink = useCallback(() => {
    if (!unlinkPending) setConfirmUnlink(null);
  }, [unlinkPending]);

  // Raw page-load counts (`webview_opened`) remain in the Event Glossary and
  // admin-only views. Partner-facing headline engagement uses unique visitors
  // plus the buyer-journey strip — not standalone "link opens".

  /**
   * Product Performance tiles — surface only metrics we can derive
   * *honestly* from the `/counts` payload. Order follows the user funnel:
   *
   *   origination  (Uploads — agent)
   *   → visibility   (Gallery Views — agent or client)
   *   → engagement   (Variations — agent or client)
   *   → distribution (Link Shares — agent or client)
   *   → export       (Downloads — agent or client)
   *
   * Sign-Ups moved to the wide "Conversion" card below this grid, where it
   * can be shown alongside the auth_wall_triggered prompt count and rate.
   *
   * "Link Shares" counts `link_copied` — the "Copy link" action inside the
   * share modal, reachable from both the editor and the gallery. This is the
   * moment the gallery link actually leaves the app. Tapping Share only opens
   * the modal and is intentionally not counted (counting it would misname the
   * action and make shares a superset of downloads). The legacy `link_shared`
   * event is never emitted by the current client but still counts for
   * historical rows (see shareEvents.js). The `share_layout` event is dead
   * code today (its caller modal is never opened from anywhere in the file).
   *
   * "Gallery Views" uses `webview_opened` — every page-load on a
   * `<SketchUpload>` viewer route, regardless of actor or source.
   *
   * "Downloads" uses `download_layout` — every export of the furnished
   * floor plan as an image.
   */

  // Editor engagement stats folded into the Shuffles tile area.
  // useEditorStats is exported from EditorEngagementCard and uses the same
  // guestSessionId fallback — no change to the hook logic.
  const editorStats = useEditorStats(demandMetricsRows);

  const shuffleCount = useMemo(() => {
    if (overviewError) return 0;
    if (!feedReplaceLoading && !feedCapped) {
      return countEventInRows('shuffle_room', demandMetricsRows);
    }
    return countFromDemand('shuffle_room');
  }, [
    overviewError,
    feedReplaceLoading,
    feedCapped,
    demandMetricsRows,
    countFromDemand,
    countEventInRows,
  ]);
  const exploredPlanCount = useMemo(() => {
    const rows = demandMetricsRows;
    if (!Array.isArray(rows) || rows.length === 0) return 0;
    const groups = groupEventsByUpload(rows);
    let count = 0;
    groups.forEach((group) => {
      const shuffles = group.rows.filter((r) => r?.meta?.event === 'shuffle_room').length;
      if (shuffles > 0) count += 1;
    });
    return count;
  }, [demandMetricsRows]);

  const heroFeaturedPlan = useMemo(() => {
    if (shuffleCount <= 0) return null;

    const statsRows = demandMetricsRows;
    const statsLookup = buildLayoutStatsFromFeed(statsRows).lookup;
    const flatPlans = flattenPartnerLayoutsFromApi(catalogLayouts || [])
      .map(normalizePartnerLayout)
      .filter(Boolean);

    let bestFromCatalog = null;
    flatPlans.forEach((plan) => {
      const periodShuffles = statsLookup(plan)?.shuffles ?? 0;
      if (
        periodShuffles > 0 &&
        (!bestFromCatalog || periodShuffles > bestFromCatalog.shuffleCount)
      ) {
        bestFromCatalog = {
          _id: plan._id || null,
          name: plan.name || 'Floor plan',
          previewImage: plan.blankImage || null,
          shuffleCount: periodShuffles,
        };
      }
    });
    if (bestFromCatalog) return bestFromCatalog;

    const groups = groupEventsByUpload(Array.isArray(statsRows) ? statsRows : []);
    let bestGroup = null;
    groups.forEach((group) => {
      const periodShuffles = group.rows.filter((r) => r?.meta?.event === 'shuffle_room').length;
      if (
        periodShuffles > 0 &&
        (!bestGroup || periodShuffles > bestGroup.shuffleCount)
      ) {
        const ref =
          group.ref && !String(group.ref).includes('not-provided') ? group.ref : null;
        bestGroup = {
          name: ref || 'Floor plan',
          previewImage: null,
          shuffleCount: periodShuffles,
        };
      }
    });
    return bestGroup;
  }, [
    shuffleCount,
    catalogLayouts,
    demandMetricsRows,
  ]);

  const heroFeaturedPlanPending =
    shuffleCount > 0 &&
    canManageListingLinks &&
    catalogLayouts.length === 0 &&
    !feedReplaceLoading;

  // Unique Visitors drill-down — opts into the per-visitor view branch in
  // MetricDrillModal via `uniqueVisitorsView: true` so any other caller of
  // the raw webview_opened drill-down (none today; defensive) stays on the
  // existing event-row list.
  const UNIQUE_VISITORS_TILE = {
    title: 'Unique Visitors',
    value: snapshotUniqueVisitors ?? 0,
    eventKey: 'webview_opened',
    uniqueVisitorsView: true,
    about: METRIC_DEFINITIONS.uniqueVisitors,
  };

  const timeRangeLabel = TIME_RANGES.find((r) => r.value === timeRange)?.label || '';

  /**
   * Business Impact tiles — locked preview. These are the outcome
   * metrics that activate once listings are linked to layouts (Serhant
   * S.MPLE integration, ETA ~2 months). Rendered as disabled tiles so
   * clients see the value-prop on day one without seeing any fabricated
   * numbers. Each tile's `sublabel` describes what it will measure; no
   * data wiring exists yet and none should be added here until the
   * integration lands.
   */
  const BUSINESS_IMPACT_TILES = [
    {
      title: 'Active Listings',
      sublabel: 'Listings using MF staging',
      icon: <HomeWorkOutlined />,
    },
    {
      title: 'Days on Market',
      sublabel: 'MF-staged vs. comparable',
      icon: <TimerOutlined />,
    },
    {
      title: 'Listing interest',
      sublabel: 'Visitors who asked to connect',
      icon: <QuestionAnswerOutlined />,
    },
    {
      title: 'Closes Influenced',
      sublabel: 'Deals attributed to MF',
      icon: <HandshakeOutlined />,
    },
  ];

  /**
   * Access gate — preserved from staging. Only admins and partner admins
   * can view this page; everyone else gets a friendly read-only message
   * instead of a blank screen.
   */
  if (!canViewAnalytics) {
    return (
      <>
        <AnalyticsPageHelmet />
        <Box
          aria-hidden="true"
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            background: ANALYTICS_PAGE_BG,
            zIndex: -1,
            pointerEvents: 'none',
          }}
        />
        <Box
          sx={{
            flexGrow: 1,
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            px: 2,
          }}
        >
        <Box
          sx={{
            maxWidth: 480,
            width: '100%',
            p: { xs: 3, sm: 4 },
            borderRadius: '14px',
            border: '1px solid rgba(0,0,0,0.06)',
            textAlign: 'center',
            bgcolor: '#fff',
          }}
        >
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: '1.1rem',
              color: 'rgba(0,0,0,0.88)',
              mb: 1,
              letterSpacing: '-0.01em',
            }}
          >
            Analytics access limited
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: 'rgba(0,0,0,0.5)', fontSize: '0.9rem', lineHeight: 1.5 }}
          >
            This page is available only to system or partner admins. Contact your
            administrator if you need access.
          </Typography>
        </Box>
      </Box>
    </>
    );
  }

  return (
    <>
      <AnalyticsPageHelmet />
      {/* Fixed gradient backdrop — compositor layer, painted once, zero scroll cost */}
      <Box
        aria-hidden="true"
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          background: ANALYTICS_PAGE_BG,
          zIndex: -1,
          pointerEvents: 'none',
        }}
      />
      <Box
        sx={{
          flexGrow: 1,
          background: 'transparent',
          minHeight: '100vh',
          pb: { xs: 'calc(env(safe-area-inset-bottom) + 32px)', sm: 6 },
          // Defeat global `button { text-transform: capitalize !important }` in index.css
          // and any Mui defaults so every label on the analytics page renders verbatim.
          '& button': { textTransform: 'none !important' },
          '&, & *': { textTransform: 'none' },
        }}
      >
      {/* ── Sticky frosted header — fade spans full main width; content stays lg ─ */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 1101,
          // Backdrop layers extend below the bar; content box size unchanged.
          overflow: 'visible',
          borderBottom: '1px solid transparent',
          py: { xs: 0.75, sm: 1 },
          mb: { xs: 1.5, sm: 2 },
          pt: { xs: 1.25, sm: 2 },
        }}
      >
        {/* Layer 1 — strong frosted backdrop at the top, soft alpha
            tint, masked to fade out toward the bottom edge so the
            feathered second layer can pick up there. */}
        <Box
          aria-hidden="true"
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: ANALYTICS_PAGE_HEADER_FADE_EXTEND_BOTTOM,
            pointerEvents: 'none',
            zIndex: -1,
            backgroundColor: 'rgba(255,255,255,0.95)',
            backdropFilter: 'saturate(180%) blur(24px)',
            WebkitBackdropFilter: 'saturate(180%) blur(24px)',
            maskImage: ANALYTICS_PAGE_HEADER_FADE_MASK_STRONG,
            WebkitMaskImage: ANALYTICS_PAGE_HEADER_FADE_MASK_STRONG,
          }}
        />
        {/* Layer 2 — gentler blur with no tint, masked so it ramps in
            under Layer 1 and trails off entirely past the bar. Two
            layers max; do not animate either blur radius. */}
        <Box
          aria-hidden="true"
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: ANALYTICS_PAGE_HEADER_FADE_EXTEND_BOTTOM,
            pointerEvents: 'none',
            zIndex: -1,
            backgroundColor: 'rgba(255,255,255,0)',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
            maskImage: 'linear-gradient(to bottom, #000, transparent)',
            WebkitMaskImage: 'linear-gradient(to bottom, #000, transparent)',
          }}
        />
        <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3 } }}>
          <Stack
            direction="row"
            alignItems="flex-start"
            justifyContent="space-between"
            spacing={1}
          >
            <Box sx={{ minWidth: 0, flex: 1, pr: 1 }}>
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: { xs: '1rem', md: '1.375rem' },
                  color: 'rgba(0,0,0,0.88)',
                  letterSpacing: '-0.02em',
                  lineHeight: 1.1,
                }}
              >
                Analytics
              </Typography>
              {isAdmin ? (
                <PartnerSubtitlePill
                  value={activePartner || 'all'}
                  partners={partnersData}
                  onChange={handleSelectPartner}
                />
              ) : (
                <Typography
                  noWrap
                  sx={{
                    fontSize: '0.7rem',
                    color: 'rgba(0,0,0,0.45)',
                    mt: 0.15,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: '100%',
                  }}
                >
                  {headerSubdomainLabel}
                </Typography>
              )}
            </Box>
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ flexShrink: 0, pt: 0.15 }}
            >
              <Tooltip title="Sync">
                <IconButton
                  onClick={handleSync}
                  size="small"
                  sx={headerIconButtonSx}
                  aria-label="Sync"
                >
                  <Refresh sx={{ fontSize: 18, color: 'rgba(0,0,0,0.5)' }} />
                </IconButton>
              </Tooltip>
              <DateRangePill
                value={timeRange}
                onChange={setTimeRange}
                onOpen={() => setFilterSheetOpen(false)}
              />
              {canViewAnalytics && (
                <Tooltip title="Filters">
                  <IconButton
                    onClick={() => setFilterSheetOpen((open) => !open)}
                    size="small"
                    sx={headerIconButtonSx}
                    aria-label="Filters"
                  >
                    <FilterList sx={{ fontSize: 18, color: 'rgba(0,0,0,0.5)' }} />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
          </Stack>
        </Container>
      </Box>

      {canViewAnalytics && (
        <AnalyticsFilterSheet
          open={filterSheetOpen}
          onClose={() => setFilterSheetOpen(false)}
          excludeInternal={excludeInternal}
          onExcludeInternalChange={setExcludeInternal}
          excludePartnerAgents={excludePartnerAgents}
          onExcludePartnerAgentsChange={setExcludePartnerAgents}
          partnerAgentEmailDomain={partnerAgentEmailDomain}
        />
      )}

      <Container
        maxWidth="lg"
        sx={{
          px: { xs: 2, sm: 3 },
          pt: 1.125,
          position: 'relative',
          isolation: 'isolate',
          // Account for the sticky frosted bar's height so a deep-link
          // (e.g. #floor-plan-library) doesn't drop the first card behind
          // the header. Both axes scroll-padded for safety.
          scrollPaddingTop: { xs: '72px', sm: '88px' },
        }}
      >
        {/* ── Error banners ─────────────────────────────────────────────── */}
        {overviewError && (
          <Box
            sx={{
              mb: 2,
              p: 1.5,
              borderRadius: '10px',
              bgcolor: analyticsRedAlpha(0.06),
              border: `1px solid ${analyticsRedAlpha(0.15)}`,
              color: ANALYTICS_RED,
              fontSize: '0.85rem',
            }}
          >
            {overviewError}
          </Box>
        )}
        {feedError && (
          <Box
            sx={{
              mb: 2,
              p: 1.5,
              borderRadius: '10px',
              bgcolor: analyticsRedAlpha(0.06),
              border: `1px solid ${analyticsRedAlpha(0.15)}`,
              color: ANALYTICS_RED,
              fontSize: '0.85rem',
            }}
          >
            {feedError}
          </Box>
        )}

        {feedCapped && (
          <Box sx={{ mb: { xs: 1.5, sm: 2 } }}>
            <FeedCapBanner maxEvents={FEED_EVENT_CAP} />
          </Box>
        )}

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' },
            gridTemplateAreas: {
              xs: '"main" "activity"',
              md: '"main activity"',
            },
            gap: { xs: 2, md: 3 },
            alignItems: 'start',
          }}
        >
          <Box sx={{ gridArea: 'main', minWidth: 0 }}>
        {isAdmin && (
          <EngagementHeroStrip
            loading={overviewLoading || feedReplaceLoading}
            shuffleCount={shuffleCount}
            exploredPlanCount={exploredPlanCount}
            uniqueVisitors={snapshotUniqueVisitors ?? 0}
            submissionsCount={submissionsCount}
            timeRangeLabel={timeRangeLabel}
            featuredPlan={heroFeaturedPlan}
            featuredPlanPending={heroFeaturedPlanPending}
            onFeaturedPlanClick={
              heroFeaturedPlan?._id && !feedReplaceLoading
                ? () => openFeaturedPlanGallery(heroFeaturedPlan)
                : null
            }
          />
        )}

        <Stack spacing={{ xs: 1.5, sm: 2 }} sx={{ width: '100%' }}>
        <AnalyticsScopeZone title="Pipeline" />
        {/* ── Floor Plan Submissions ────────────────────────────────────── */}
        {overviewLoading || !submissionFetchSettled ? (
          <Skeleton
            variant="rounded"
            height={120}
            sx={{ borderRadius: '14px', bgcolor: 'rgba(0,0,0,0.04)' }}
          />
        ) : (
          <ButtonBase
            onClick={() => openMetric(SUBMISSIONS_TILE)}
            disabled={false}
            sx={{
              position: 'relative',
              width: '100%',
              textAlign: 'left',
              display: 'block',
              minHeight: { xs: 132, sm: 140 },
              // !important defeats global `button { border-radius: 50px !important }` in index.css
              borderRadius: '14px !important',
              border: '1px solid rgba(0,0,0,0.06)',
              bgcolor: '#ffffff',
              p: { xs: 1.75, sm: 2 },
              transition: 'background 160ms ease, border-color 160ms ease, transform 120ms ease',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.015)', borderColor: 'rgba(0,0,0,0.1)' },
              '&:active': { transform: 'scale(0.997)' },
            }}
          >
            {/* Label row. The "i" tooltip is intentionally absent on
                clickable cards — tap target collided with the card's
                onClick on mobile. The same copy now lives at the top of
                the drill-down modal as a visible "About" section. */}
            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: { xs: 1, sm: 1.25 }, pr: 3 }}>
              <FileUploadOutlined sx={{ fontSize: 15, color: 'rgba(0,0,0,0.4)', flexShrink: 0 }} />
              <Typography
                sx={{
                  fontSize: { xs: '0.875rem', md: '0.9375rem' },
                  fontWeight: 500,
                  color: 'rgba(0,0,0,0.5)',
                  mr: 0.25,
                }}
              >
                Floor Plan Submissions
              </Typography>
            </Stack>
            {/* Body: hero stat + ring on right */}
            <Stack direction="row" alignItems="flex-end" justifyContent="space-between" spacing={1.5}>
              {/* Left: headline count + secondary breakdown */}
              <Box sx={{ minWidth: 0 }}>
                {/* Headline */}
                <Stack direction="row" alignItems="baseline" spacing={1.25} sx={{ mb: 0.25 }}>
                  <Box>
                    <Typography sx={ANALYTICS_HEADLINE_NUMBER_SX}>
                      {submissionsCount}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: { xs: '0.75rem', md: '0.8125rem' },
                        fontWeight: 400,
                        color: 'rgba(0,0,0,0.45)',
                        mt: 0.3,
                      }}
                    >
                      {timeRangeLabel
                        ? `in the ${timeRangeLabel.toLowerCase()}`
                        : 'in this period'}
                    </Typography>
                  </Box>
                </Stack>

                {/* Secondary breakdown — chips for processed/failed + plain avg time row */}
                <Stack
                  direction="row"
                  sx={{ mt: { xs: 1, sm: 1.25 }, flexWrap: 'wrap', gap: '6px 8px' }}
                >
                  {/* Processed chip */}
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={0.5}
                    sx={{ bgcolor: 'rgba(0,0,0,0.04)', borderRadius: '20px', px: 1, py: 0.5 }}
                  >
                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: ANALYTICS_GREEN, flexShrink: 0 }} />
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 500, color: 'rgba(0,0,0,0.55)', lineHeight: 1, textTransform: 'none' }}>
                      {processedCount} processed
                    </Typography>
                  </Stack>
                  {/* Failed chip — always shown so the status is clear */}
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={0.5}
                    sx={{ bgcolor: 'rgba(0,0,0,0.04)', borderRadius: '20px', px: 1, py: 0.5 }}
                  >
                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: ANALYTICS_RED, flexShrink: 0 }} />
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 500, color: 'rgba(0,0,0,0.55)', lineHeight: 1, textTransform: 'none' }}>
                      {failedCount} need attention
                    </Typography>
                  </Stack>
                </Stack>

                {/* Avg processing time — plain row below chips. When there
                    are failures in this period we surface "(X did not
                    finish)" inline so the partner reading the time does not
                    miss that the average only describes successful runs. */}
                {/* Avg processing time. The "did not finish" caption used to
                    live here too but it duplicated the red "X failed" chip
                    above. The chip is enough; the modal's About section
                    explains that the time covers successful runs only. */}
                {submissionsCount > 0 && avgProcessingSeconds != null && (
                  <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.75, flexWrap: 'wrap', rowGap: '4px' }}>
                    <AccessTimeOutlined sx={{ fontSize: 13, color: 'rgba(0,0,0,0.35)', flexShrink: 0 }} />
                    <Typography sx={{ fontSize: '0.75rem', color: 'rgba(0,0,0,0.45)', lineHeight: 1.2 }}>
                      Avg. Processing Time: {formatProcessingTime(avgProcessingSeconds)}
                    </Typography>
                  </Stack>
                )}
              </Box>

              {/* Right: circular success ring — aligned to flex-end so it sits near
                  the bottom of the card and clears the absolute-positioned chevron */}
              {submissionsCount > 0 && successRate >= 20 && (
                <Box sx={{ pb: { xs: 0.5, sm: 0.75 }, pr: 0 }}>
                  <RingProgress
                    value={successRate}
                    size={100}
                    strokeColor={ringColor}
                    labelFontSize="0.9rem"
                    sublabel="success"
                  />
                </Box>
              )}
            </Stack>

            <ChevronRight
              sx={{
                position: 'absolute',
                top: { xs: 15, sm: 19 },
                right: { xs: 11, sm: 15 },
                fontSize: 18,
                color: 'rgba(0,0,0,0.3)',
                opacity: 0.45,
                pointerEvents: 'none',
              }}
            />
          </ButtonBase>
        )}

        <AnalyticsScopeZone title="Audience" />
        <InquiriesActivityCard
          drilldownFeedRows={demandMetricsRows}
          contextRows={feedRows}
          catalogLayouts={catalogForInquiries}
          catalogLookupLoading={catalogLookupLoading}
          excludeInternal={excludeInternal}
          excludePartnerAgents={excludePartnerAgents}
          partnerAgentEmailDomain={partnerAgentEmailDomain}
          dateLabel={timeRangeLabel}
          dateParams={dateParams}
          partnerNameFilter={partnerNameFilter}
          usersPartnerFilter={usersPartnerFilter}
        />

        <UniqueVisitorsCard
          feedRows={demandMetricsRows}
          loading={feedReplaceLoading}
          visitorStats={visitorStats}
          audienceFiltered={demandScopeFilterActive}
          feedFetchSettled={feedFetchSettled}
          periodFrom={chartPeriodBounds.from}
          periodTo={chartPeriodBounds.to}
          feedCapped={feedCapped}
          timeRangeLabel={timeRangeLabel}
          identityContextRows={feedRows}
          userDirectory={visitorUserDirectory}
          onClick={
            feedFetchSettled ? () => openMetric(UNIQUE_VISITORS_TILE) : undefined
          }
        />

        <EngagementCard
          feedRows={demandMetricsRows}
          loading={feedReplaceLoading}
          shuffleCount={shuffleCount}
          interactiveActiveMedianSeconds={editorStats.interactiveActiveMedianSeconds}
          dateLabel={timeRangeLabel}
          excludeInternal={excludeInternal}
          excludePartnerAgents={excludePartnerAgents}
          partnerAgentEmailDomain={partnerAgentEmailDomain}
          partnerAdminUsers={partnerAdminUsers}
        />

        <AnalyticsScopeZone title="Results" />
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 2fr' },
            gap: { xs: 1.5, sm: 2 },
            width: '100%',
            minWidth: 0,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
        {overviewLoading ? (
          <Skeleton
            variant="rounded"
            height={88}
            sx={{ borderRadius: '14px', bgcolor: 'rgba(0,0,0,0.04)' }}
          />
        ) : (
          <ButtonBase
            onClick={() => openMetric(CONVERSION_TILE)}
            sx={{
              position: 'relative',
              width: '100%',
              textAlign: 'left',
              display: 'block',
              borderRadius: '14px !important',
              border: '1px solid rgba(0,0,0,0.06)',
              bgcolor: '#ffffff',
              p: { xs: 1.75, sm: 2 },
              transition: 'background 160ms ease, border-color 160ms ease, transform 120ms ease',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.015)', borderColor: 'rgba(0,0,0,0.1)' },
              '&:active': { transform: 'scale(0.997)' },
            }}
          >
            {/* Label row */}
            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: { xs: 1, sm: 1.25 }, pr: 3 }}>
              <PersonAddOutlined sx={{ fontSize: 15, color: 'rgba(0,0,0,0.4)', flexShrink: 0 }} />
              <Typography
                sx={{
                  fontSize: { xs: '0.875rem', md: '0.9375rem' },
                  fontWeight: 500,
                  color: 'rgba(0,0,0,0.5)',
                }}
              >
                Sign-Ups
              </Typography>
            </Stack>

            {/* Outcome-first card: count of new accounts plus period subline. */}
            <Typography sx={ANALYTICS_HEADLINE_NUMBER_SX}>
              {signupCount.toLocaleString()}
            </Typography>
            <Typography
              sx={{
                fontSize: { xs: '0.75rem', md: '0.8125rem' },
                fontWeight: 400,
                color: 'rgba(0,0,0,0.45)',
                mt: 0.3,
                lineHeight: 1.4,
              }}
            >
              {timeRangeLabel
                ? `in the ${timeRangeLabel.toLowerCase()}`
                : 'in this period'}
            </Typography>
            <ChevronRight
              sx={{
                position: 'absolute',
                top: { xs: 15, sm: 19 },
                right: { xs: 11, sm: 15 },
                fontSize: 18,
                color: 'rgba(0,0,0,0.3)',
                opacity: 0.45,
                pointerEvents: 'none',
              }}
            />
          </ButtonBase>
        )}
          </Box>

          <Box sx={{ minWidth: 0, width: '100%' }}>
        {/* Shares/downloads tiles and drill-down share one full-window fetch
            (filtered client-side). Recent Activity below stays unfiltered. */}
        <SharingActivityCard
          drilldownFeedRows={demandMetricsRows}
          contextRows={feedRows}
          excludeInternal={excludeInternal}
          excludePartnerAgents={excludePartnerAgents}
          partnerAdminUsers={partnerAdminUsers}
          partnerAgentEmailDomain={partnerAgentEmailDomain}
          dateLabel={timeRangeLabel}
          dateParams={dateParams}
          partnerNameFilter={partnerNameFilter}
          usersPartnerFilter={usersPartnerFilter}
        />
          </Box>
        </Box>
        </Stack>

        {!isMdUp && (
          <Box sx={{ mt: { xs: 4.5, sm: 2 } }}>
            <RecentActivitySection
              recentActivityOpen={recentActivityOpen}
              setRecentActivityOpen={setRecentActivityOpen}
              isPartnerAdmin={isPartnerAdmin}
              feedRows={feedRows}
              handleExportFeedCSV={handleExportFeedCSV}
              feedFetchSettled={feedFetchSettled}
              timeRange={timeRange}
              partnerNameFilter={partnerNameFilter}
              feedTotal={feedTotal}
              handleLoadMore={handleLoadMore}
              feedLoading={feedLoading}
              setActiveFeedRow={setActiveFeedRow}
              setActiveRowGalleryUrl={setActiveRowGalleryUrl}
              layoutRefByUuid={layoutRefByUuid}
              layoutUuidByMongoId={layoutUuidByMongoId}
            />
          </Box>
        )}

        {isAdmin && (!activePartner || activePartner === 'all') && (
          <Box
            sx={{
              mt: { xs: 1.5, sm: 2 },
              px: { xs: 1.75, sm: 2 },
              py: { xs: 1.25, sm: 1.5 },
              borderRadius: '14px',
              border: '1px dashed rgba(0,0,0,0.12)',
              bgcolor: 'rgba(0,0,0,0.02)',
            }}
          >
            <Typography sx={{ fontSize: '0.78rem', color: 'rgba(0,0,0,0.5)', lineHeight: 1.55 }}>
              Select a partner above to link floor plans to listing IDs and view per-listing metrics.
            </Typography>
          </Box>
        )}

        {canManageListingLinks && (
          <>
            <AnalyticsScopeZone
              title="Listing performance"
              caption={listingPerformanceCaption}
              sx={{ mt: { xs: 5, sm: 6 } }}
            />
            <LinkedListingsSummaryBand
              periodRollup={linkedListingsPeriodRollup}
              lifetimeRollup={linkedListingsLifetimeRollup}
              linkedCount={filteredLinkedCount}
              statusFilterLabel={linkedStatusFilterLabel}
              timeRangeLabel={timeRangeLabel}
              loading={listingsSummaryLoading && linkedPlanCount > 0}
            />
            <FloorPlanLibrarySection
            partnerHostname={partnerNameFilter}
            partnerId={listingLinkPartnerId}
            timeRange={timeRange}
            timeRangeLabel={timeRangeLabel}
            catalogRefreshKey={sync}
            submissionFeedRows={submissionRowsRaw}
            listingLinks={listingLayoutLinks}
            listingsSummaryPeriod={listingsSummaryPeriod}
            listingsSummaryLifetime={listingsSummaryLifetime}
            linkedStatusFilter={linkedStatusFilter}
            onLinkedStatusFilterChange={setLinkedStatusFilter}
            partner={partnerNameFilter}
            existingLinks={listingLayoutLinks}
            planByCustomerRef={planByCustomerRef}
            onAttachPlan={(plan) => openAttachModal(plan)}
            onChangePlan={openManageForLinkedPlan}
            onDisconnect={requestUnlink}
            onLinkByUrl={() => setLinkByUrlOpen(true)}
            onListingLinkSuccess={handleListingLinkSuccess}
            onEditListingStatus={openListingManage}
            linkNoticeMessage={linkSuccessMsg}
            linkNoticeIsError={linkNoticeIsError}
            onDismissLinkNotice={clearLinkNotice}
            onCatalogLoaded={handleCatalogLoaded}
            layoutStatsLookup={listingLayoutStatsWithLineage.lookupDirect}
            layoutLineagePeriodLookup={listingLayoutStatsWithLineage.lookupLineagePeriod}
            layoutLineageLifetimeLookup={listingLayoutStatsWithLineage.lookupLineageLifetime}
          />
          </>
        )}

        {isAdmin && (
          <PlatformUsageSection
            monthlySummary={monthlySummary}
            monthlyGenerationLimit={monthlyGenerationLimit}
            hasRequestedData={monthlySummaryRequested}
          />
        )}

        {isAdmin && (
        <Box
          sx={{
            mt: { xs: 3, sm: 3.5 },
            borderRadius: '14px',
            border: '1px solid rgba(0,0,0,0.06)',
            bgcolor: '#fff',
            overflow: 'hidden',
          }}
        >
          <ButtonBase
            onClick={() => setBusinessImpactOpen((v) => !v)}
            aria-expanded={businessImpactOpen}
            sx={{
              width: '100%',
              textAlign: 'left',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: { xs: 1.75, sm: 2 },
              py: { xs: 1.25, sm: 1.5 },
              borderRadius: '0 !important',
              textTransform: 'none !important',
            }}
          >
            <Stack direction="row" alignItems="center" spacing={0.75}>
              <LockOutlined sx={{ fontSize: 14, color: 'rgba(0,0,0,0.35)' }} />
              <Box>
                <Typography
                  sx={{
                    fontSize: '0.8125rem',
                    fontWeight: 600,
                    color: 'rgba(0,0,0,0.65)',
                  }}
                >
                  Coming soon: Business Impact & Listings
                </Typography>
                <Typography sx={{ fontSize: '0.72rem', color: 'rgba(0,0,0,0.42)', mt: 0.2 }}>
                  Preview only — activates when listings are linked
                </Typography>
              </Box>
            </Stack>
            <ExpandMore
              sx={{
                fontSize: 20,
                color: 'rgba(0,0,0,0.4)',
                transform: businessImpactOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 200ms ease',
              }}
            />
          </ButtonBase>
          <Collapse in={businessImpactOpen} unmountOnExit>
            <Box sx={{ px: { xs: 1.75, sm: 2 }, pb: { xs: 1.75, sm: 2 } }}>
              <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                {BUSINESS_IMPACT_TILES.map((tile) => (
                  <Grid item xs={6} sm={3} key={tile.title}>
                    <MetricCard
                      title={tile.title}
                      value="—"
                      icon={tile.icon}
                      disabled={true}
                    />
                  </Grid>
                ))}
              </Grid>
              {!isPartnerAdmin && (
                <>
                  <ButtonBase
                    onClick={() => setSampleListingOpen((v) => !v)}
                    aria-expanded={sampleListingOpen}
                    sx={{
                      width: '100%',
                      textAlign: 'left',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.75,
                      mt: { xs: 1.5, sm: 2 },
                      p: { xs: 1.25, sm: 1.5 },
                      borderRadius: '12px !important',
                      border: '1px dashed rgba(0,0,0,0.12)',
                      bgcolor: 'rgba(0,0,0,0.015)',
                      textTransform: 'none !important',
                    }}
                  >
                    <ExpandMore
                      sx={{
                        fontSize: 16,
                        color: 'rgba(0,0,0,0.45)',
                        transition: 'transform 200ms ease',
                        transform: sampleListingOpen ? 'rotate(0deg)' : 'rotate(-90deg)',
                      }}
                    />
                    <Typography sx={{ fontSize: '0.78rem', fontWeight: 500, color: 'rgba(0,0,0,0.55)' }}>
                      {sampleListingOpen ? 'Hide sample listing layout' : 'Preview sample listing layout'}
                    </Typography>
                  </ButtonBase>
                  <Collapse in={sampleListingOpen} timeout="auto" unmountOnExit>
                    <Box sx={{ mt: { xs: 1.25, sm: 1.5 } }}>
                      {feedFetchSettled ? (
                        <ListingsView
                          rows={feedRows}
                          loading={false}
                          hasMore={feedRows.length < feedTotal}
                          loadingMore={feedLoading && feedRows.length > 0}
                          onLoadMore={handleLoadMore}
                          subtitle={subtitle}
                        />
                      ) : (
                        <Skeleton variant="rounded" height={160} sx={{ bgcolor: 'rgba(0,0,0,0.04)' }} />
                      )}
                    </Box>
                  </Collapse>
                </>
              )}
            </Box>
          </Collapse>
        </Box>
        )}

        {!isPartnerAdmin && (
          <Grid container spacing={{ xs: 1.5, sm: 2 }} sx={{ mt: { xs: 1.5, sm: 2 } }}>
            <Grid item xs={12}>
              <EventLegend categoryCounts={categoryCounts} />
            </Grid>
          </Grid>
        )}
          </Box>

          <Box
            sx={{
              gridArea: 'activity',
              display: { xs: 'none', md: 'block' },
              position: { md: 'sticky' },
              top: { md: '88px' },
              minWidth: 0,
            }}
          >
            <RecentActivitySection
              recentActivityOpen={recentActivityOpen}
              setRecentActivityOpen={setRecentActivityOpen}
              isPartnerAdmin={isPartnerAdmin}
              feedRows={feedRows}
              handleExportFeedCSV={handleExportFeedCSV}
              feedFetchSettled={feedFetchSettled}
              timeRange={timeRange}
              partnerNameFilter={partnerNameFilter}
              feedTotal={feedTotal}
              handleLoadMore={handleLoadMore}
              feedLoading={feedLoading}
              setActiveFeedRow={setActiveFeedRow}
              setActiveRowGalleryUrl={setActiveRowGalleryUrl}
              layoutRefByUuid={layoutRefByUuid}
              layoutUuidByMongoId={layoutUuidByMongoId}
            />
          </Box>
        </Box>

        {canManageListingLinks && (
          <AttachListingModal
            open={attachModalOpen}
            onClose={() => {
              setAttachModalOpen(false);
              setAttachSelectedPlan(null);
            }}
            partner={partnerNameFilter}
            selectedPlan={attachSelectedPlan}
            initialListingId={attachListingId}
            existingLinks={listingLayoutLinks}
            planByCustomerRef={planByCustomerRef}
            mlsSyncEnabled={false}
            onSuccess={handleListingLinkSuccess}
          />
        )}

        {canManageListingLinks && (
          <LinkByUrlModal
            open={linkByUrlOpen}
            onClose={() => setLinkByUrlOpen(false)}
            partner={partnerNameFilter}
            existingLinks={listingLayoutLinks}
            planByCustomerRef={planByCustomerRef}
            mlsSyncEnabled={false}
            onSuccess={handleListingLinkSuccess}
          />
        )}

        {canManageListingLinks && (
          <ListingManageModal
            open={statusSetupOpen}
            onClose={closeListingStatusSetup}
            partner={partnerNameFilter}
            linkId={statusSetupTarget?.linkId}
            listingRef={statusSetupTarget?.listingRef || ''}
            planName={statusSetupTarget?.planName}
            initialMlsStatus={statusSetupTarget?.mlsStatus || 'unknown'}
            initialCustomPropertyStatus={statusSetupTarget?.customPropertyStatus || ''}
            initialOnMarketDate={statusSetupTarget?.onMarketDate}
            initialOnMarketDateSource={statusSetupTarget?.onMarketDateSource}
            initialOffMarketDate={statusSetupTarget?.offMarketDate}
            initialMlsSyncEnabled={Boolean(statusSetupTarget?.mlsSyncEnabled)}
            onSuccess={handleListingLinkSuccess}
          />
        )}

        {canManageListingLinks && (
          <UnlinkConfirmDialog
            open={Boolean(confirmUnlink)}
            plan={confirmUnlink?.plan || null}
            listingRef={confirmUnlink?.listingRef || ''}
            onCancel={cancelUnlink}
            onConfirm={handleConfirmUnlink}
            pending={unlinkPending}
          />
        )}

        <MetricDrillModal
          open={Boolean(activeMetric)}
          onClose={() => setActiveMetric(null)}
          metric={activeMetric}
          rows={
            activeMetric?.eventKey === 'floorplan_uploaded'
              ? supplyMetricsRows
              : activeMetric?.eventKey === 'account_created'
              ? accountMetricsRows
              : demandMetricsRows
          }
          contextRows={feedRows}
          excludeInternal={excludeInternal}
          excludePartnerAgents={excludePartnerAgents}
          partnerAgentEmailDomain={partnerAgentEmailDomain}
          partnerAdminUsers={partnerAdminUsers}
          userDirectory={visitorUserDirectory}
          loading={
            activeMetric?.eventKey === 'account_created'
              ? false
              : activeMetric?.eventKey === 'floorplan_uploaded'
              ? !submissionFetchSettled
              : feedReplaceLoading
          }
          feedActivityReady={feedActivityReady}
          feedCapped={feedCapped}
          feedEventCap={FEED_EVENT_CAP}
          submissionSourceRows={supplyMetricsRows}
          submissionSourceReady={submissionFetchSettled}
          dateLabel={TIME_RANGES.find((r) => r.value === timeRange)?.label}
          dateParams={dateParams}
          partnerNameFilter={partnerNameFilter}
          usersPartnerFilter={usersPartnerFilter}
          timeRange={timeRange}
          avgProcessingSeconds={avgProcessingSeconds}
          conversionRate={activeMetric?.eventKey === 'account_created' ? conversionRate : null}
          effectivePrompted={activeMetric?.eventKey === 'account_created' ? effectivePrompted : null}
          catalogLayouts={catalogLayouts}
        />
        <EventDetailModal
          open={Boolean(activeFeedRow)}
          onClose={() => {
            setActiveFeedRow(null);
            setActiveRowGalleryUrl(null);
          }}
          row={activeFeedRow}
          groupGalleryUrl={activeRowGalleryUrl}
        />

        {isAdmin && (
          <PartnerAnalyticsGuide
            onLinkListing={canManageListingLinks ? () => setLinkByUrlOpen(true) : null}
          />
        )}

        {/* ── Legal strip ─────────────────────────────────────────────── */}
        <Box
          sx={{
            textAlign: 'center',
            pt: { xs: 5, sm: 6 },
            pb: { xs: 3, sm: 4 },
          }}
        >
          <Box
            component="span"
            sx={{ fontSize: '10px', color: 'rgba(0,0,0,0.3)' }}
          >
            <Box
              component="a"
              href={MF_TERMS_URL}
              target="_blank"
              rel="noopener noreferrer"
              sx={legalLinkSx}
            >
              Terms
            </Box>
            {' · '}
            <Box
              component="a"
              href={MF_PRIVACY_URL}
              target="_blank"
              rel="noopener noreferrer"
              sx={legalLinkSx}
            >
              Privacy
            </Box>
            {' · '}
            <Box
              component="a"
              href={MF_SUPPORT_URL}
              target="_blank"
              rel="noopener noreferrer"
              sx={legalLinkSx}
            >
              Support
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
    </>
  );
};

export default Analytics;
